import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import SEOHead from './SEO/SEOHead';
import SchemaMarkup from './SEO/SchemaMarkup';
import SectionTitle from './shared/SectionTitle';

const AboutPage: React.FC = () => {
    return (
        <Box sx={{ py: 6 }}>
            <SEOHead page="about" />
            <SchemaMarkup type="organization" />
            <Container maxWidth="md">
                <SectionTitle sx={{ mb: 3 }}>
                    Our Story
                </SectionTitle>
                
                <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.7 }}>
                    Point Sails was founded in 1991 by Chip Buck, but its origins trace back even earlier—to the late 1980s in the Columbia River Gorge. There, Chip met Daron White, a dedicated windsurfing sailmaker who lived and worked in a converted barn with a one-room sail loft. Daron was already immersed in the art of sailmaking, cutting panels by hand and testing every sail on the water whenever the wind picked up.
                </Typography>
                
                <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.7 }}>
                    Chip, drawn to both the sport and the craftsmanship, spent those formative years under Daron's mentorship. He learned the fundamentals of shaping, sewing, and tuning sails—not in a classroom, but directly on the water and at the sewing machine. By 1991, Chip had begun designing and building sails of his own, launching Point Sails out of his mother's garage in San Diego. The goal was simple but ambitious: to build sails that performed exceptionally well and reflected the hands-on, high-performance spirit of his roots.
                </Typography>
                
                <Typography variant="body1" sx={{ mb: 6, lineHeight: 1.7 }}>
                    From that origin story, Point Sails has grown into a respected sail loft delivering custom sails for over 7,000 boats, blending decades of on-the-water experience with modern tools and digital design precision.
                </Typography>
                
                <SectionTitle sx={{ mt: 6, mb: 3 }}>
                    Meet the Team
                </SectionTitle>
                
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Box
                        component="img"
                        src="/images/ChipPS.jpg"
                        alt="Chip Buck"
                        sx={{
                            width: 150,
                            height: 150,
                            borderRadius: 2,
                            objectFit: 'cover',
                            flexShrink: 0,
                            alignSelf: { xs: 'center', sm: 'flex-start' },
                            border: '2px solid #1a3a5c',
                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
                        }}
                    />
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Chip Buck – San Diego, CA
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', mb: 1 }}>
                            Sail design, production oversight, and fulfillment
                        </Typography>
                        <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                            Chip has over 30 years of hands-on sail design experience, having produced thousands of sails for cruising and performance sailors. Every Point Sails design comes directly from his expertise. Beginning with windsurfing sails in the late '80s and evolving through his time at North Sails One Design and Hobie Cat Co., he personally oversees the design and customer experience, ensuring that every Point Sails order meets the high performance and quality standards that have defined his career.
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', mb: 6, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Box
                        component="img"
                        src="/images/DaronPS.jpg"
                        alt="Daron White"
                        sx={{
                            width: 150,
                            height: 150,
                            borderRadius: 2,
                            objectFit: 'cover',
                            flexShrink: 0,
                            alignSelf: { xs: 'center', sm: 'flex-start' },
                            border: '2px solid #1a3a5c',
                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
                        }}
                    />
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Daron White – Lincoln City, OR
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', mb: 1 }}>
                            Operations, technical, and customer support
                        </Typography>
                        <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                            Daron handles the technology, order processing, and customer communications that keep Point Sails running smoothly. Not only Chip's early mentor—he's a lifelong collaborator and now the technical architect behind Point Sails' newest chapter. He's building the backend systems that power quoting, measurement, and order management, integrating decades of IT experience into a system that will scale for the future.
                        </Typography>
                    </Box>
                </Box>
                
                <SectionTitle sx={{ mt: 6, mb: 3 }}>
                    What Sets Point Sails Apart
                </SectionTitle>
                
                <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                    Legacy + Innovation
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                    Every sail is backed by real-world experience—Chip and Daron have both built and flown sails in conditions from Baja to the Gorge. Their combined history informs every cut, curve, and cloth choice.
                </Typography>
                
                <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                    Tailored for You
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                    Whether you're buying sails for a Beneteau cruiser or a Hobie beach cat, Point Sails offers custom-fit solutions with detailed rig measurement forms, guided color selections, and smart quoting tools powered by a database of 7,000+ sailboat models.
                </Typography>
                
                <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                    No Hype, Just Performance
                </Typography>
                <Typography variant="body1" sx={{ mb: 6, lineHeight: 1.7 }}>
                    This isn't mass production—it's boutique sailmaking by people who care deeply about the final product. Orders are reviewed personally. Customer questions are answered by the sailmaker himself. The result? Sails that perform, last, and earn loyalty.
                </Typography>
                
                <SectionTitle sx={{ mt: 6, mb: 3 }}>
                    Our Mission
                </SectionTitle>

                <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    Point Sails exists to serve sailors who want real quality—built by people who understand the sea, the wind, and the importance of getting it right. From the first conversation to the final stitch, we make sure the process is clear, collaborative, and built on trust.
                </Typography>
            </Container>
        </Box>
    );
};

export default AboutPage;
